import { z } from 'zod';

export const SUBSCRIPTION_PERIOD = 30 * 24 * 60 * 60;
export const REEL_PACKAGES = [
  { id: 'start', videos: 3, title: 'Старт', stars: 390, rub: 490, tributeSubscriptionId: 265286, tributeUrl: 'https://t.me/tribute/app?startapp=s170O' },
  { id: 'active', videos: 10, title: 'Автор', stars: 1000, rub: 1290, popular: true, tributeSubscriptionId: 265338, tributeUrl: 'https://t.me/tribute/app?startapp=s171E' },
  { id: 'factory', videos: 30, title: 'Про', stars: 2600, rub: 3290, tributeSubscriptionId: 265331, tributeUrl: 'https://t.me/tribute/app?startapp=s171x' },
] as const;
export const REEL_ADDON = {
  id: 'addon', videos: 3, title: 'Докупка', stars: 390, rub: 490,
  tributeProductId: 157489, tributeUrl: 'https://t.me/tribute/app?startapp=pEY9',
} as const;
export const REEL_ADDONS = [
  REEL_ADDON,
  { id: 'addon10', videos: 10, title: 'Докупка', stars: 1000, rub: 1290,
    tributeProductId: 163914, tributeUrl: 'https://t.me/tribute/app?startapp=pGDM' },
  { id: 'addon30', videos: 30, title: 'Докупка', stars: 2600, rub: 3290,
    tributeProductId: 163916, tributeUrl: 'https://t.me/tribute/app?startapp=pGDO' },
] as const;
export interface BillingAddon {
  id: string; title?: string; videos: number; stars: number; rub: number;
  available: boolean; starsAvailable: boolean; tributeAvailable: boolean;
}
// Historical Tribute product mappings must never change the purchased entitlement.
export const LEGACY_REEL_PACKAGES = [
  { id: 'start', videos: 10, stars: 1100 },
  { id: 'active', videos: 25, stars: 2500 },
  { id: 'factory', videos: 50, stars: 4500 },
] as const;
export type PaymentProvider = 'stars' | 'tribute';
export interface BillingSummary {
  enabled: boolean;
  available: number;
  reserved: number;
  packages: Array<{ id: string; videos: number; title: string; stars: number; rub: number; popular?: boolean; tribute: boolean }>;
  subscription: { provider?: PaymentProvider; id: string; packageId: string; periodEnd: string; cancelled: boolean; changeRequiresCancellation?: boolean } | null;
  balances: { carried: number; current: number; addon: number; frozen: number; trial: number; legacy: number; referral: number; promo: number; debt: number };
  addon: { videos: number; stars: number; rub: number; available: boolean; starsAvailable: boolean; tributeAvailable: boolean };
  addons?: BillingAddon[];
  starsEnabled: boolean;
  tributeEnabled: boolean;
  history: Array<{ id: string; delta: number; kind: string; createdAt: string }>;
}

export const BillingEnvSchema = z.object({
  TRIBUTE_CHECKOUT_ENABLED: z.enum(['true', 'false']).default('false').transform(v => v === 'true'),
  BILLING_ENABLED: z.enum(['true', 'false']).default('false').transform(v => v === 'true'),
  // A real-bot checkout for one operator account while everyone else stays on free beta.
  BILLING_TEST_TG_ID: z.string().trim().default('').transform(v => v === '' ? null : Number(v))
    .refine(v => v === null || (Number.isSafeInteger(v) && v > 0), 'BILLING_TEST_TG_ID must be a positive Telegram user ID'),
  TRIBUTE_SUBSCRIPTION_START: z.coerce.number().int().nonnegative().default(265286),
  TRIBUTE_SUBSCRIPTION_ACTIVE: z.coerce.number().int().nonnegative().default(265338),
  TRIBUTE_SUBSCRIPTION_FACTORY: z.coerce.number().int().nonnegative().default(265331),
  TRIBUTE_API_KEY: z.string().trim().default(''),
  // Product IDs are permanent: create a new product when changing the number of reels.
  TRIBUTE_PRODUCT_START: z.coerce.number().int().nonnegative().default(0),
  TRIBUTE_PRODUCT_ACTIVE: z.coerce.number().int().nonnegative().default(0),
  TRIBUTE_PRODUCT_FACTORY: z.coerce.number().int().nonnegative().default(0),
  TRIBUTE_PRODUCT_ADDON: z.coerce.number().int().nonnegative().default(157489),
  TRIBUTE_PRODUCT_ADDON10: z.coerce.number().int().nonnegative().default(163914),
  TRIBUTE_PRODUCT_ADDON30: z.coerce.number().int().nonnegative().default(163916),
}).refine(v => !(v.BILLING_ENABLED && v.BILLING_TEST_TG_ID !== null),
  'Use either BILLING_ENABLED or BILLING_TEST_TG_ID, not both').refine(v => {
  const ids = [v.TRIBUTE_PRODUCT_START, v.TRIBUTE_PRODUCT_ACTIVE, v.TRIBUTE_PRODUCT_FACTORY,
    v.TRIBUTE_PRODUCT_ADDON, v.TRIBUTE_PRODUCT_ADDON10, v.TRIBUTE_PRODUCT_ADDON30].filter(Boolean);
  return new Set(ids).size === ids.length;
}, 'Tribute product IDs must be unique').refine(v => {
  const ids = [v.TRIBUTE_SUBSCRIPTION_START, v.TRIBUTE_SUBSCRIPTION_ACTIVE, v.TRIBUTE_SUBSCRIPTION_FACTORY].filter(Boolean);
  return new Set(ids).size === ids.length;
}, 'Tribute subscription IDs must be unique');

export type BillingConfig = z.infer<typeof BillingEnvSchema>;
export function billingEnabledFor(config: BillingConfig, tgId: number): boolean {
  return config.BILLING_ENABLED || (config.BILLING_TEST_TG_ID !== null && config.BILLING_TEST_TG_ID === tgId);
}
export function tributeProductId(config: BillingConfig, packageId: string): number {
  return ({ start: config.TRIBUTE_PRODUCT_START, active: config.TRIBUTE_PRODUCT_ACTIVE, factory: config.TRIBUTE_PRODUCT_FACTORY,
    addon: config.TRIBUTE_PRODUCT_ADDON, addon10: config.TRIBUTE_PRODUCT_ADDON10, addon30: config.TRIBUTE_PRODUCT_ADDON30 } as Record<string, number>)[packageId] ?? 0;
}

export function tributeSubscriptionId(config: BillingConfig, packageId: string): number {
  return ({ start: config.TRIBUTE_SUBSCRIPTION_START, active: config.TRIBUTE_SUBSCRIPTION_ACTIVE, factory: config.TRIBUTE_SUBSCRIPTION_FACTORY } as Record<string, number>)[packageId] ?? 0;
}
